import React from "react";
import { Hexagon, Minus, Tag, Ruler, Square, MapPin } from "lucide-react";
import { useT } from "../../lib/lang";
import { formatDistance, formatArea, lineLengthMeters, polygonAreaSqMeters } from "../../lib/planificationGeo";

// Iter99 — Phase 2C §7.1: icon + measure-label per object_type/purpose,
// shared by the list AND the properties form header so both agree.
const ICON_BY_TYPE = { polygon: Hexagon, line: Minus, annotation: Tag };
const TYPE_LABEL_KEY = {
  "polygon:drawing": "planificationTypePolygon",
  "polygon:measurement": "planificationTypeMeasureArea",
  "line:drawing": "planificationTypeLine",
  "line:measurement": "planificationTypeMeasureDistance",
  "annotation:drawing": "planificationTypeAnnotation",
};

export const iconForFeature = (feature) => {
  if (feature.properties.purpose === "measurement") return feature.properties.object_type === "polygon" ? Square : Ruler;
  return ICON_BY_TYPE[feature.properties.object_type] || MapPin;
};

export const measureLabelForFeature = (feature) => {
  try {
    if (feature.properties.object_type === "polygon") return formatArea(polygonAreaSqMeters(feature));
    if (feature.properties.object_type === "line") return formatDistance(lineLengthMeters(feature));
  } catch (_e) { /* mid-transition geometry — no label yet */ }
  return null;
};

/**
 * Object list for the side panel's "Outils de dessin" section (spec
 * §7.1/§7.2). Purely a view over `featureCollection.features` — no
 * local state, no duplication risk by construction since it re-renders
 * straight off the SAME array that create/update/delete/undo/redo all
 * mutate (spec §7.3 "aucune duplication d'entrée").
 */
export const PlanificationObjectList = ({ features, selectedId, onSelect }) => {
  const [t] = useT();

  if (!features.length) {
    return (
      <p className="px-3 pb-3 text-[11px] text-zinc-500 italic" data-testid="planification-object-list-empty">
        {t.planificationNoObjectsCreated}
      </p>
    );
  }

  return (
    <ul className="flex flex-col" data-testid="planification-object-list">
      {features.map((feature) => {
        const Icon = iconForFeature(feature);
        const typeLabel = t[TYPE_LABEL_KEY[`${feature.properties.object_type}:${feature.properties.purpose}`]] || feature.properties.object_type;
        const measureLabel = measureLabelForFeature(feature);
        const isSelected = feature.id === selectedId;
        return (
          <li key={feature.id}>
            <button
              type="button"
              data-testid={`planification-object-item-${feature.id}`}
              onClick={() => onSelect(feature.id)}
              aria-pressed={isSelected}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-zinc-800/40 transition-colors ${
                isSelected ? "bg-emerald-500/10 text-emerald-300" : "text-zinc-300 hover:bg-zinc-900/60"
              }`}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: feature.properties.color }} aria-hidden="true" />
              <Icon className="w-3.5 h-3.5 shrink-0 text-zinc-500" />
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-medium truncate">{feature.properties.name || typeLabel}</span>
                <span className="block text-[10px] text-zinc-500">
                  {typeLabel}{measureLabel ? ` — ${measureLabel}` : ""}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
